/**
 * Condition identity, in one place. A condition exists in three representations and they must agree:
 *
 * | Where | Form | Example |
 * | --- | --- | --- |
 * | HTML `name` attribute | `<predicate>:<ref>[.<choiceKey>]` | `assume:entity.sweden` |
 * | CKEditor marker name | `condition-inline:<name>:<instanceId>` | `condition-inline:assume:entity.sweden:9f2a…` |
 * | Compile step | splits the name on `:` then on `.` | `values.entity.sweden` |
 *
 * The `instanceId` exists only in the marker name: the HTML permits two conditions to share an id
 * while CKEditor marker names must be unique. It is deliberately dropped on the way out.
 *
 * The middle portion of a marker name is treated as **verbatim**: split off the prefix and the
 * instance id, never re-assembled from parsed parts, so names this module cannot make sense of still
 * round-trip exactly.
 */
import { v4 as uuid } from 'uuid';
export const CONDITION_INLINE_PREFIX = 'condition-inline';
export const CONDITION_BLOCK_PREFIX = 'condition-block';
export const CONDITION_PREDICATES = ['assume', 'unless'];
function isPredicate(value) {
    return CONDITION_PREDICATES.includes(value);
}
/** Builds the HTML `name` attribute. */
export function buildConditionName(condition) {
    const { predicate, ref, choiceKey } = condition;
    return choiceKey ? `${predicate}:${ref}.${choiceKey}` : `${predicate}:${ref}`;
}
/**
 * Parses an HTML `name` attribute. Unparseable names fall back to `assume` with the whole string
 * as the ref, which keeps them visible and editable rather than silently dropped.
 */
export function parseConditionName(name) {
    const separator = name.indexOf(':');
    if (separator === -1) {
        return { predicate: 'assume', ref: name };
    }
    const predicate = name.slice(0, separator);
    const target = name.slice(separator + 1);
    const dot = target.indexOf('.');
    const ref = dot === -1 ? target : target.slice(0, dot);
    const choiceKey = dot === -1 ? undefined : target.slice(dot + 1);
    return {
        predicate: isPredicate(predicate) ? predicate : 'assume',
        ref,
        choiceKey: choiceKey || undefined,
    };
}
export function isConditionMarker(markerName) {
    return markerName.startsWith(`${CONDITION_INLINE_PREFIX}:`) || markerName.startsWith(`${CONDITION_BLOCK_PREFIX}:`);
}
export function buildMarkerName(name, instanceId = uuid()) {
    return `${CONDITION_INLINE_PREFIX}:${name}:${instanceId}`;
}
export function buildMarkerNameFor(condition, instanceId) {
    return buildMarkerName(buildConditionName(condition), instanceId);
}
/**
 * Splits a marker name into prefix / verbatim name / instance id, then parses the name.
 * Returns `null` for anything that is not a three-or-more segment condition marker.
 */
export function parseMarkerName(markerName) {
    if (!isConditionMarker(markerName)) {
        return null;
    }
    const segments = markerName.split(':');
    // a marker name is build like `prefix:name:instance_id`.
    if (segments.length < 3) {
        return null;
    }
    const prefix = segments[0];
    const instanceId = segments[segments.length - 1];
    const name = segments.slice(1, -1).join(':');
    return Object.assign({ markerName, prefix, name, instanceId }, parseConditionName(name));
}
